import React, { useEffect, useState } from 'react'
import { Modal } from './ui/Modal'
import { Button } from './ui/Button'
import { EditableList } from './ui'
import { API_BASE_URL, IVPositionFilterOptions } from '../data'
import { Guideline, GuidelineModule, GuidelineFormModalMetaProps } from '../data/types'

type Props = {
    meta: GuidelineFormModalMetaProps
    isOpen: boolean
    onClose: () => void
    onSubmit: (guidelineData: any) => Promise<void>
    initialGuideline?: Guideline | null
}

const emptyModule = (): GuidelineModule & { _id: string } => ({
    _id: `module-${Date.now()}-${Math.random()}`,
    type: 'document',
    title: '',
    file_key: '',
    description: ''
})

export const GuidelineFormModal: React.FC<Props> = ({ meta, isOpen, onClose, onSubmit, initialGuideline }) => {
    const [name, setName] = useState('')
    const [description, setDescription] = useState('')
    const [modules, setModules] = useState<(GuidelineModule & { _id: string })[]>([])
    const [audienceMode, setAudienceMode] = useState<'all' | 'service_record'>('all')
    const [selectedServiceRecords, setSelectedServiceRecords] = useState<string[]>([])
    const [uploadingFiles, setUploadingFiles] = useState<Set<string>>(new Set())
    const [loading, setLoading] = useState(false)
    const [error, setError] = useState<string | null>(null)

    useEffect(() => {
        if (initialGuideline) {
            setName(initialGuideline.name)
            setDescription(initialGuideline.description)
            setModules(initialGuideline.content.modules.map(m => ({ ...m, _id: `module-${Math.random()}` })))
            const rule = initialGuideline.audience_rules?.find(r => r.type === 'service_record')
            setAudienceMode(rule ? 'service_record' : 'all')
            setSelectedServiceRecords(rule?.values ?? [])
        } else {
            setName(''); setDescription(''); setModules([])
            setAudienceMode('all'); setSelectedServiceRecords([])
        }
    }, [initialGuideline, isOpen])

    const handleFileUpload = async (moduleId: string, file: File) => {
        try {
            setUploadingFiles(prev => new Set(prev).add(moduleId))
            setError(null)

            const response = await fetch(`${API_BASE_URL}/file`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ filename: file.name, folder: 'guidelines' })
            })
            if (!response.ok) throw new Error('Failed to get upload URL')
            const result = await response.json()
            const { url: presignedUrl, key } = result.data || result

            const uploadResponse = await fetch(presignedUrl, {
                method: 'PUT',
                body: file,
                headers: { 'Content-Type': file.type }
            })
            if (!uploadResponse.ok) throw new Error('Failed to upload file')

            setModules(prev => prev.map(m => m._id === moduleId ? { ...m, file_key: key } : m))
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to upload file')
        } finally {
            setUploadingFiles(prev => { const s = new Set(prev); s.delete(moduleId); return s })
        }
    }

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setLoading(true)
        setError(null)
        try {
            const cleanedModules = modules.map(({ _id, ...m }) => m)
            const audience_rules = audienceMode === 'service_record' && selectedServiceRecords.length > 0
                ? [{ type: 'service_record', values: selectedServiceRecords }]
                : null

            await onSubmit({
                name,
                description,
                content: { modules: cleanedModules }, // plain object — not stringified
                audience_rules
            })
            onClose()
        } catch (err) {
            setError(err instanceof Error ? err.message : 'Failed to save guideline')
        } finally {
            setLoading(false)
        }
    }

    const footer = (
        <div className='flex gap-3 justify-end'>
            <Button variant="outline" onClick={onClose} disabled={loading}>{meta.cancelButton}</Button>
            <Button type="submit" form="guideline-form" disabled={loading}>
                {loading ? meta.submittingButton : meta.submitButton}
            </Button>
        </div>
    )

    return (
        <Modal isOpen={isOpen} onClose={onClose} title={meta.title} footer={footer} maxWidth="2xl">
            <form id="guideline-form" onSubmit={handleSubmit} className="px-6 py-4 space-y-4">
                {error && <div className="text-red-600 text-sm">{error}</div>}

                <div>
                    <label className="block text-sm font-medium mb-1">{meta.nameLabel}</label>
                    <input value={name} onChange={e => setName(e.target.value)} placeholder={meta.namePlaceholder}
                        className="w-full border rounded-lg px-3 py-2" required />
                </div>

                <div>
                    <label className="block text-sm font-medium mb-1">{meta.descriptionLabel}</label>
                    <textarea value={description} onChange={e => setDescription(e.target.value)}
                        placeholder={meta.descriptionPlaceholder} className="w-full border rounded-lg px-3 py-2" required />
                </div>

                {/* Audience */}
                <div>
                    <label className="block text-sm font-medium mb-2">{meta.audienceLabel}</label>
                    <div className="flex gap-4 mb-2">
                        <label className="flex items-center gap-2">
                            <input type="radio" checked={audienceMode === 'all'}
                                onChange={() => setAudienceMode('all')} />
                            {meta.audienceAllOption}
                        </label>
                        <label className="flex items-center gap-2">
                            <input type="radio" checked={audienceMode === 'service_record'}
                                onChange={() => setAudienceMode('service_record')} />
                            {meta.audienceServiceRecordOption}
                        </label>
                    </div>
                    {audienceMode === 'service_record' && (
                        <div className="bg-gray-50 dark:bg-gray-600 border rounded-lg p-3 h-40 overflow-y-auto space-y-2">
                            {IVPositionFilterOptions.map(sr => (
                                <label key={sr} className="flex items-center gap-2 cursor-pointer">
                                    <input type="checkbox" checked={selectedServiceRecords.includes(sr)}
                                        onChange={() => setSelectedServiceRecords(prev =>
                                            prev.includes(sr) ? prev.filter(s => s !== sr) : [...prev, sr]
                                        )} />
                                    <span className="text-sm">{sr}</span>
                                </label>
                            ))}
                        </div>
                    )}
                </div>

                {/* Modules (PDF only for now) */}
                <EditableList
                    items={modules}
                    onItemsChange={setModules}
                    getItemId={m => m._id}
                    createNewItem={emptyModule}
                    addButtonLabel={meta.addModuleButton}
                    emptyMessage={meta.noModulesMessage}
                    itemLabel="Document"
                    renderItem={(module, index) => (
                        <div className="space-y-2">
                            <input value={module.title}
                                onChange={e => setModules(prev => prev.map(m => m._id === module._id ? { ...m, title: e.target.value } : m))}
                                placeholder="Title" className="w-full border rounded-lg px-3 py-2" />
                            <input type="file" accept="application/pdf"
                                disabled={uploadingFiles.has(module._id)}
                                onChange={e => e.target.files?.[0] && handleFileUpload(module._id, e.target.files[0])} />
                            {uploadingFiles.has(module._id) && <span className="text-sm text-gray-500">{meta.uploadingLabel}</span>}
                            {module.file_key && <span className="text-xs text-green-600">Uploaded ✓</span>}
                        </div>
                    )}
                />
            </form>
        </Modal>
    )
}
